import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { useSelector } from "react-redux";
import { selectOrders } from "../../../../../../store/selectors/OrderSlice";
import { COLORS } from "../../../../../../utils/Colors";
import { Order, OrderState } from "../../../../../../entity/Order";
import "./OrderStateComponent.css";
import { useTranslation } from "react-i18next";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const OrderStateComponent = () => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const orders = useSelector(selectOrders);

  const { t } = useTranslation();

  // Get last 6 months including current month
  const getLastSixMonths = () => {
    const result = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentYear, currentMonth - i, 1);
      result.push({
        month: months[date.getMonth()],
        year: date.getFullYear(),
        monthIndex: date.getMonth(),
      });
    }
    return result;
  };

  const lastSixMonths = getLastSixMonths();
  const monthLabels = lastSixMonths.map((m) => `${m.month} ${m.year}`);

  // Process orders data for last 6 months
  const monthlyData = lastSixMonths.map(({ monthIndex, year }) => {
    const monthOrders = orders.filter((order: Order) => {
      if (!order.createdAt) return false;
      const orderDate = new Date(order.createdAt);
      return (
        orderDate.getMonth() === monthIndex && orderDate.getFullYear() === year
      );
    });

    return {
      month: months[monthIndex],
      total: monthOrders.length,
      delivered: monthOrders.filter((o) => o.status === OrderState.DELIVERED)
        .length,
      returned: monthOrders.filter((o) => o.status === OrderState.RETURNED)
        .length,
      pending: monthOrders.filter(
        (o) =>
          o.status !== OrderState.DELIVERED && o.status !== OrderState.RETURNED,
      ).length,
    };
  });

  // Calculate summary statistics
  const totalOrders = monthlyData.reduce((sum, d) => sum + d.total, 0);
  const totalDelivered = monthlyData.reduce((sum, d) => sum + d.delivered, 0);
  const totalReturned = monthlyData.reduce((sum, d) => sum + d.returned, 0);
  const deliveryRate =
    totalOrders > 0 ? ((totalDelivered / totalOrders) * 100).toFixed(1) : "0";
  const returnRate =
    totalOrders > 0 ? ((totalReturned / totalOrders) * 100).toFixed(1) : "0";

  const data = {
    labels: monthLabels,
    datasets: [
      {
        label: t("stats.order.total_orders"),
        data: monthlyData.map((d) => d.total),
        backgroundColor: COLORS.Brand.Primary,
        borderRadius: 6,
        borderSkipped: false,
      },
      {
        label: t("stats.order.delivered"),
        data: monthlyData.map((d) => d.delivered),
        backgroundColor: COLORS.Brand.Secondary,
        borderRadius: 6,
        borderSkipped: false,
      },
      {
        label: t("stats.order.returned"),
        data: monthlyData.map((d) => d.returned),
        backgroundColor: COLORS.Brand.Accent,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        align: "end" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 16,
          font: {
            size: 13,
            family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            weight: "500" as const,
          },
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 13,
          weight: "600" as const,
        },
        bodyFont: {
          size: 12,
        },
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ${context.raw} ${t("orders")}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.06)",
          drawBorder: false,
        },
        ticks: {
          stepSize: 1,
          font: {
            size: 11,
          },
          padding: 8,
        },
        border: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 11,
            weight: "500" as const,
          },
          padding: 8,
        },
        border: {
          display: false,
        },
      },
    },
    barPercentage: 0.7,
    categoryPercentage: 0.8,
  };

  return (
    <div className="order-stats-container">
      {/* Header Section */}
      <div className="stats-header">
        <div className="header-content">
          <h2 className="stats-title">{t("stats.order.title")}</h2>
          <p className="stats-subtitle">{t("stats.order.subtitle")}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="stat-card">
          <div className="stat-icon total">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">{t("stats.order.total_orders")}</p>
            <p className="stat-value">{totalOrders}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon delivered">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">{t("stats.order.delivered")}</p>
            <p className="stat-value">
              {totalDelivered}{" "}
              <span className="stat-percentage success">{deliveryRate}%</span>
            </p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon returned">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="9 14 4 9 9 4" />
              <path d="M20 20v-7a4 4 0 00-4-4H4" />
            </svg>
          </div>
          <div className="stat-content">
            <p className="stat-label">{t("stats.order.returned")}</p>
            <p className="stat-value">
              {totalReturned}{" "}
              <span className="stat-percentage warning">{returnRate}%</span>
            </p>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="chart-wrapper">
        <div className="chart-header">
          <h3 className="chart-title">{t("stats.order.monthly_breakdown")}</h3>
        </div>
        <div className="chart-container">
          <Bar options={options as any} data={data} />
        </div>
      </div>
    </div>
  );
};
